import { get, post, put } from "aws-amplify/api";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import MergerSubsDetailList from "containers/mergersubs/MergerSubsDetailList";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

const columnHelper = createColumnHelper();

function MergerSubsList({ isAuthenticated, user }) {
  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [mergerSubs, setMergerSubs] = useState([]);
  const [mergerSubDetails, setMergerSubDetails] = useState([]);
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [cityscope, setCityscope] = useState(null);
  const [appendix, setAppendix] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedMergerSubId, setSelectedMergerSubId] = useState(null);

  const typeOptions = useMemo(
    () => [
      { value: "S", label: "Sub Division" },
      { value: "M", label: "Merger" },
      { value: "N", label: "New Property" },
      { value: "R", label: "Removed Property" },
    ],
    []
  );

  const extractBody = async (result) => {
    try {
      if (result?.body) {
        return await result.body.json();
      }

      if (result?.response?.body) {
        return await result.response.body.json();
      }

      return result;
    } catch (error) {
      console.error("Failed to parse API response", error);
      return { result: false, error_message: "Network Error" };
    }
  };

  const getCityscopes = useCallback(async () => {
    const response = await get({ apiName: "colin", path: "/cityscopes/dropdown" }).response;
    return extractBody(response);
  }, []);

  const getMergerSubs = useCallback(async (filter = {}) => {
    const filteredParams = Object.fromEntries(
      Object.entries(filter).filter(([_, value]) => value !== null && value !== "")
    );

    const response = await get({
      apiName: "colin",
      path: "/mergersubs",
      options: { queryParams: filteredParams },
    }).response;
    return extractBody(response);
  }, []);

  const validateRelatedRecord = useCallback((record, setName) => {
    if (setName === "mergerSubs") {
      if (!record.appendix || String(record.appendix).trim() === "") {
        return [false, "You must enter an appendix."];
      }

      if (!record.type || String(record.type).trim() === "") {
        return [false, "You must select a type of change."];
      }
    }

    if (setName === "mergerSubDetails") {
      if (!record.property_key || String(record.property_key).trim() === "") {
        return [false, "Enter a cityscope reference."];
      }
    }

    return [true, ""];
  }, []);

  const saveMergerSub = useCallback(
    async (record) => {
      const payload = { ...record, last_edited_by: user };
      if (payload.updated !== undefined && payload.updated !== null) {
        payload.updated = payload.updated === true || payload.updated === "true" || payload.updated === 1;
      }

      if (payload.new_record) {
        const response = await post({
          apiName: "colin",
          path: "/mergersubs/new",
          options: { body: payload },
        }).response;
        return extractBody(response);
      }

      const response = await put({
        apiName: "colin",
        path: `/mergersubs/${payload.merger_sub_id}`,
        options: { body: payload },
      }).response;
      return extractBody(response);
    },
    [user]
  );

  const saveRelatedRecord = useCallback(
    async (record, endpoint, idField) => {
      const payload = { ...record, last_edited_by: user };
      const endpointSlug = String(endpoint).toLowerCase();

      if (payload.new_record) {
        const response = await post({
          apiName: "colin",
          path: `/mergersubs/${payload.merger_sub_id}/${endpointSlug}/new`,
          options: { body: payload },
        }).response;
        return extractBody(response);
      }

      const response = await put({
        apiName: "colin",
        path: `/mergersubs/${payload.merger_sub_id}/${endpointSlug}/${payload[idField]}`,
        options: { body: payload },
      }).response;
      return extractBody(response);
    },
    [user]
  );

  const syncDetailsToParent = useCallback(
    (newDetails, explicitRowIndex = null) => {
      setMergerSubDetails(newDetails);
      setMergerSubs((prev) => {
        const next = [...prev];
        const rowIndex =
          explicitRowIndex ??
          (selectedMergerSubId
            ? prev.findIndex((row) => row.merger_sub_id === selectedMergerSubId)
            : selectedRowIndex);

        if (rowIndex >= 0 && rowIndex < next.length) {
          next[rowIndex] = {
            ...next[rowIndex],
            mergersubs_list: newDetails,
          };
        }

        return next;
      });
    },
    [selectedMergerSubId, selectedRowIndex]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const initialize = async () => {
      setIsLoading(true);
      try {
        const localCityscopes = await getCityscopes();
        if (mounted && Array.isArray(localCityscopes)) {
          setCityscopeOptions(localCityscopes);
        }
      } catch (error) {
        console.error(error);
        if (mounted) {
          setUi({ showAlert: true, errorMessage: "Unable to load cityscopes." });
        }
      } finally {
        if (mounted) {
          document.title = "Mergers & Sub Divisions";
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [getCityscopes, isAuthenticated]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMergerSubDetails([]);
    setSelectedMergerSubId(null);
    setSelectedRowIndex(null);
    setIsLoading(true);
    setUi((prev) => ({ ...prev, showAlert: false, errorMessage: "" }));

    try {
      const localData = await getMergerSubs({ cityscope });
      const sortedData = [...(localData || [])].sort((a, b) => {
        return (
          (a.created_date === null) - (b.created_date === null) ||
          -(a.created_date > b.created_date) ||
          +(a.created_date < b.created_date)
        );
      });
      setMergerSubs(sortedData);
    } catch (error) {
      console.error(error);
      setUi({ showAlert: true, errorMessage: "Unable to load merger/sub records." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCityscopeChange = (selected) => {
    setSelectedCityscope(selected);
    setCityscope(selected?.value ?? null);
    setAppendix(selected?.appendix ?? null);
  };

  const updateTableState = (rowIndex, columnId, value) => {
    setMergerSubs((old) =>
      old.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value, modified: true } : row
      )
    );
  };

  const handleSave = async ({ row, column, value }) => {
    const rowIndex = row.index;
    const recordToSave = { ...row.original, [column.id]: value };
    const [isValid, validationMessage] = validateRelatedRecord(recordToSave, "mergerSubs");

    setUi((prev) => ({ ...prev, showAlert: false, errorMessage: "" }));

    if (!isValid) {
      setUi({ showAlert: true, errorMessage: validationMessage });
      return false;
    }

    if (recordToSave.isSaving) {
      return true;
    }

    setMergerSubs((old) =>
      old.map((record, idx) =>
        idx === rowIndex ? { ...record, [column.id]: value, isSaving: true } : record
      )
    );

    try {
      const result = await saveMergerSub(recordToSave);
      if (!result?.result) {
        setMergerSubs((old) =>
          old.map((record, idx) =>
            idx === rowIndex ? { ...record, isSaving: false } : record
          )
        );
        setUi({
          showAlert: true,
          errorMessage: result?.error_message || "Network Error: Could not save record.",
        });
        return false;
      }

      setMergerSubs((old) =>
        old.map((record, idx) => {
          if (idx !== rowIndex) {
            return record;
          }

          const nextRecord = {
            ...record,
            [column.id]: value,
            modified: false,
            isSaving: false,
            new_record: false,
          };

          if (record.new_record && result.id) {
            nextRecord.merger_sub_id = result.id;
          }

          return nextRecord;
        })
      );

      if (recordToSave.new_record && result.id) {
        setSelectedMergerSubId(result.id);
        setMergersubIdOnDetails(result.id, rowIndex);
      }

      return true;
    } catch (error) {
      console.error(error);
      setMergerSubs((old) =>
        old.map((record, idx) =>
          idx === rowIndex ? { ...record, isSaving: false } : record
        )
      );
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save record." });
      return false;
    }
  };

  const setMergersubIdOnDetails = (newMergerSubId, rowIndex) => {
    if (!newMergerSubId || selectedRowIndex !== rowIndex) {
      return;
    }

    const hydratedDetails = mergerSubDetails.map((detail) => ({
      ...detail,
      merger_sub_id: newMergerSubId,
    }));
    syncDetailsToParent(hydratedDetails, rowIndex);
  };

  const addRecord = () => {
    const newRecord = {
      merger_sub_id: null,
      cityscope: cityscope || "",
      appendix: appendix || "",
      type: "M",
      description: "",
      updated: false,
      mergersubs_list: [],
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
    };

    setMergerSubs((prev) => [newRecord, ...prev]);
    setSelectedRowIndex(0);
    setSelectedMergerSubId(null);
    setMergerSubDetails([]);
  };

  const handleSelectRecord = (row) => {
    const selected = row.original;
    setSelectedRowIndex(row.index);
    setSelectedMergerSubId(selected?.merger_sub_id ?? null);
    setMergerSubDetails(selected?.mergersubs_list || []);
  };

  const refreshDetails = useCallback(async () => {
    if (!cityscope) {
      return;
    }

    try {
      const localData = await getMergerSubs({ cityscope });
      const sortedData = [...(localData || [])].sort((a, b) => {
        return (
          (a.created_date === null) - (b.created_date === null) ||
          -(a.created_date > b.created_date) ||
          +(a.created_date < b.created_date)
        );
      });

      setMergerSubs(sortedData);

      if (!selectedMergerSubId) {
        setMergerSubDetails([]);
        setSelectedRowIndex(null);
        return;
      }

      const refreshedRowIndex = sortedData.findIndex(
        (row) => row.merger_sub_id === selectedMergerSubId
      );

      if (refreshedRowIndex >= 0) {
        setSelectedRowIndex(refreshedRowIndex);
        setMergerSubDetails(sortedData[refreshedRowIndex]?.mergersubs_list || []);
      } else {
        setSelectedRowIndex(null);
        setSelectedMergerSubId(null);
        setMergerSubDetails([]);
      }
    } catch (error) {
      console.error(error);
    }
  }, [cityscope, getMergerSubs, selectedMergerSubId]);

  const handleUpdateArticleLinks = async (row) => {
    const record = row.original;

    if (!record?.merger_sub_id) {
      setUi({ showAlert: true, errorMessage: "Save the row before updating article links." });
      return;
    }

    try {
      await post({
        apiName: "colin",
        path: `/mergersubs/${record.merger_sub_id}/updatelinks`,
        options: { body: record },
      }).response;

      const nextRows = mergerSubs.map((item, idx) =>
        idx === row.index ? { ...item, updated: true, modified: true } : item
      );
      setMergerSubs(nextRows);
      await saveMergerSub(nextRows[row.index]);
    } catch (error) {
      console.error(error);
      setUi({ showAlert: true, errorMessage: "Unable to update article links." });
    }
  };

  const handleDeleteRecord = async (row) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this record?");
    if (!confirmDelete) {
      return;
    }

    const record = row.original;
    if (record.new_record || !record.merger_sub_id) {
      const nextRows = mergerSubs.filter((_, idx) => idx !== row.index);
      setMergerSubs(nextRows);
      if (selectedRowIndex === row.index) {
        setSelectedMergerSubId(null);
        setSelectedRowIndex(null);
        setMergerSubDetails([]);
      }
      return;
    }

    try {
      const result = await saveMergerSub({
        ...record,
        archived_date: new Date(),
        modified: true,
      });

      if (!result?.result) {
        setUi({
          showAlert: true,
          errorMessage: result?.error_message || "Failed to delete record.",
        });
        return;
      }

      const nextRows = mergerSubs.filter((_, idx) => idx !== row.index);
      setMergerSubs(nextRows);

      if (selectedRowIndex === row.index) {
        setSelectedMergerSubId(null);
        setSelectedRowIndex(null);
        setMergerSubDetails([]);
      }
    } catch (error) {
      console.error(error);
      setUi({ showAlert: true, errorMessage: "Failed to delete record." });
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("merger_sub_id", {
        id: "merger_sub_id",
        header: "ID",
        cell: (info) => (
          <Button type="button" size="sm" variant="link" onClick={() => handleSelectRecord(info.row)}>
            {info.getValue() || "New"}
          </Button>
        ),
        size: 90,
        enableSorting: false,
      }),
      columnHelper.accessor("cityscope", {
        id: "cityscope",
        header: "Cityscope",
        cell: EditableCell,
        meta: { options: cityscopeOptions },
        size: 110,
      }),
      columnHelper.accessor("appendix", {
        id: "appendix",
        header: "Appendix",
        cell: EditableCell,
        size: 110,
      }),
      columnHelper.accessor("type", {
        id: "type",
        header: "Type",
        cell: EditableCell,
        meta: { options: typeOptions },
        size: 120,
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "Description",
        cell: EditableCell,
        size: 250,
      }),
      columnHelper.accessor("updated", {
        id: "updated",
        header: "Links Updated",
        cell: EditableCell,
        meta: { type: "checkbox", align: "center" },
        size: 100,
      }),
      columnHelper.accessor("created_date", {
        id: "created_date",
        header: "Created Date",
        cell: (info) => {
          const value = info.getValue();
          return value ? Moment(value).local().format("DD-MM-YYYY") : "";
        },
        size: 110,
      }),
      columnHelper.display({
        id: "article_links",
        header: "",
        cell: (info) => {
          if (info.row.original.updated || !info.row.original.merger_sub_id) {
            return null;
          }
          return (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => handleUpdateArticleLinks(info.row)}
            >
              Update Article Links
            </Button>
          );
        },
        size: 120,
        enableSorting: false,
      }),
      columnHelper.display({
        id: "delete_record",
        header: "",
        cell: (info) => (
          <Button type="button" size="sm" variant="danger" onClick={() => handleDeleteRecord(info.row)}>
            Delete
          </Button>
        ),
        size: 80,
        enableSorting: false,
      }),
    ],
    [cityscopeOptions, mergerSubs, typeOptions]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="mergersubs">
      <Modal show={ui.showAlert} onHide={() => setUi((prev) => ({ ...prev, showAlert: false }))} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-0">
            {ui.errorMessage}
          </Alert>
        </Modal.Body>
      </Modal>

      <Form onSubmit={handleSubmit}>
        <Row className="mergersubs-filter-row">
          <Col sm={4} md={4} lg={2}>
            <Form.Group controlId="cityscope">
              <Select
                value={selectedCityscope}
                onChange={handleCityscopeChange}
                options={cityscopeOptions}
                placeholder="Cityscope"
                className="mergersubs-cityscope-select"
                classNamePrefix="mergersubs-cityscope-select"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Button type="submit" variant="primary" className="mergersubs-search-button">
              Search
            </Button>
          </Col>
        </Row>
      </Form>

      <Row className="show-Container mt-3">
        <Col sm={8} md={8} lg={8}>
          <Row className="mb-2">
            <Col className="text-right">
              <Button variant="secondary" size="sm" onClick={addRecord}>
                Add
              </Button>
            </Col>
          </Row>
          <GenericTable
            columns={columns}
            data={mergerSubs}
            pageSize={PAGE_SIZES.mergerSubs}
            onRowClick={handleSelectRecord}
            rowClassName={(row) =>
              row.index === selectedRowIndex && row.original?.merger_sub_id ? "mergersubs-selected-row" : ""
            }
            meta={{
              updateData: updateTableState,
              handleSave,
            }}
          />
        </Col>

        <Col>
          <MergerSubsDetailList
            isAuthenticated={isAuthenticated}
            user={user}
            data={mergerSubDetails}
            mergersubid={selectedMergerSubId}
            updateTableState={syncDetailsToParent}
            saveRecord={saveRelatedRecord}
            validateRelatedRecord={validateRelatedRecord}
            refreshDetails={refreshDetails}
          />
        </Col>
      </Row>
    </div>
  );
}

export default MergerSubsList;